import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Button,
  Input,
  Form,
  Divider,
  Space,
  Tag,
  Descriptions,
  Tabs,
  Switch,
  message,
  Spin,
  theme,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  EditOutlined,
  SaveOutlined,
  LockOutlined,
  BellOutlined,
  SecurityScanOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";

const { Title, Text, Paragraph } = Typography;

export default function Profile() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // Password change flow state
  const [passwordStep, setPasswordStep] = useState(1); // 1: verify current, 2: enter code + new password
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");

  // Profile data state
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    address: "",
    website: "",
    avatar: null,
    accountStatus: "Active",
    memberSince: "",
  });

  // Check if user is company admin (can edit company address/website)
  const roleType = localStorage.getItem("roleType") || "";
  const isCompanyAdmin = roleType.toLowerCase().includes("admin");

  // Notification settings
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    dispatchNotifications: true,
    systemAlerts: true,
    weeklyReport: false,
    marketingEmails: false,
  });

  // Set form values when entering edit mode (after Form is rendered)
  useEffect(() => {
    if (isEditing) {
      const editableData = { ...profileData };
      Object.keys(editableData).forEach((key) => {
        if (editableData[key] === "Not Available" || editableData[key] === "Not provided") {
          editableData[key] = "";
        }
      });
      console.log('[Profile] useEffect - setting form values:', editableData);
      // Use setTimeout to ensure Form is fully rendered
      setTimeout(() => {
        form.setFieldsValue(editableData);
        console.log('[Profile] After setFieldsValue, form values:', form.getFieldsValue());
      }, 0);
    }
  }, [isEditing, form, profileData]);

  // Fetch profile and notification settings
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);

        // Fetch profile data using centralized api instance
        const res = await api.get("/api/profile");
        if (res.data.success) {
          const data = res.data.data;
          setProfileData({
            name: data.name || "User",
            email: data.email || "",
            phone: data.phone || "",
            company: data.company || "",
            role: data.role || "User",
            address: data.address || "",
            website: data.website || "",
            avatar: data.avatar || null,
            accountStatus: data.accountStatus || "Active",
            memberSince: data.memberSince || "",
          });
        }

        // TODO: Fetch notification settings when notification feature is ready
        // try {
        //   const notifRes = await api.get("/api/profile/notifications");
        //   if (notifRes.data.success && notifRes.data.data) {
        //     setNotifications(notifRes.data.data);
        //   }
        // } catch (notifErr) {
        //   console.error("Error fetching notification settings:", notifErr);
        // }
      } catch (err) {
        console.error("Error fetching profile:", err);
        // 401 errors are handled automatically by the api interceptor (redirects to login)
        // For other errors, show a message to the user
        if (!err.message?.includes("Unauthorized")) {
          message.error("Failed to load profile data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleEdit = () => {
    // Just set editing mode - form values will be set by useEffect after Form renders
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    form.resetFields();
  };

  const handleSaveProfile = async () => {
    // Get form values
    let values;
    try {
      values = await form.validateFields();
      console.log('[Profile] Validated values:', values);
    } catch (validationError) {
      console.log('[Profile] Validation failed:', validationError);
      // Even if validation fails, get the values that exist
      values = validationError.values || form.getFieldsValue();
      if (!values || Object.keys(values).length === 0) {
        message.error('Please fill in the required fields');
        return;
      }
    }

    // Ensure we have data to send
    if (!values || Object.keys(values).filter(k => values[k] !== undefined).length === 0) {
      console.log('[Profile] No values to send, using profileData');
      values = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        website: profileData.website,
      };
    }

    console.log('[Profile] Sending to API:', JSON.stringify(values));

    try {
      setSaving(true);
      const res = await api.put("/api/profile", values);

      console.log('[Profile] Response status:', res.status);

      if (res.data.success) {
        const updatedData = res.data.data || values;
        setProfileData({ ...profileData, ...updatedData });
        setIsEditing(false);
        message.success("Profile updated successfully");
      } else {
        message.error(res.data.message || "Failed to update profile");
      }
    } catch (err) {
      console.error("Error saving profile:", err);
      const serverMsg = err.response?.data?.message || "Failed to update profile";
      message.error(serverMsg);
    } finally {
      setSaving(false);
    }
  };

  // Step 1: Verify current password and send 2FA code
  const handleVerifyCurrentPassword = async () => {
    try {
      const currentPassword = passwordForm.getFieldValue("currentPassword");
      if (!currentPassword) {
        message.error("Please enter current password");
        return;
      }

      setVerifyingPassword(true);
      const res = await api.post("/api/auth/verify-current-password", { currentPassword });

      if (res.data.success) {
        setMaskedEmail(res.data.email || "");
        setPasswordStep(2);
        message.success("Verification code sent to your email");
      } else {
        message.error(res.data.message || "Failed to verify password");
      }
    } catch (err) {
      console.error("Error verifying password:", err);
      const serverMsg = err.response?.data?.message || "Failed to verify password";
      message.error(serverMsg);
    } finally {
      setVerifyingPassword(false);
    }
  };

  // Step 2: Submit new password with verification code
  const handleChangePassword = async (values) => {
    try {
      setSaving(true);
      const res = await api.post("/api/auth/change-password", {
        code: values.verificationCode,
        newPassword: values.newPassword,
      });

      if (res.data.success) {
        passwordForm.resetFields();
        setPasswordStep(1);
        setMaskedEmail("");
        message.success("Password changed successfully");
      } else {
        message.error(res.data.message || "Failed to change password");
      }
    } catch (err) {
      console.error("Error changing password:", err);
      const serverMsg = err.response?.data?.message || "Failed to change password";
      message.error(serverMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPasswordChange = () => {
    passwordForm.resetFields();
    setPasswordStep(1);
    setMaskedEmail("");
  };

  const handleNotificationChange = async (key, value) => {
    const updatedNotifications = { ...notifications, [key]: value };
    setNotifications(updatedNotifications);

    try {
      const res = await api.put("/api/profile/notifications", updatedNotifications);

      if (res.data.success) {
        message.success("Notification settings updated");
      } else {
        // Revert on failure
        setNotifications(notifications);
        message.error(res.data.message || "Failed to update notification settings");
      }
    } catch (err) {
      console.error("Error updating notification settings:", err);
      setNotifications(notifications);
      const serverMsg = err.response?.data?.message || "Failed to update notification settings";
      message.error(serverMsg);
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleColor = (role) => {
    const roleColors = {
      "Powow Admin": "purple",
      "Powow Operation": "blue",
      "Partner Admin": "green",
      "Partner Operation": "cyan",
      User: "default",
    };
    return roleColors[role] || "default";
  };

  if (loading) {
    return (
      <PageLayout active="profile">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  // Consistent min-height for all tab contents (matches Notifications with 5 items)
  const tabContentStyle = { minHeight: 420 };

  const tabItems = [
    {
      key: "profile",
      label: "Profile Information",
      children: (
        <div style={tabContentStyle}>
          {isEditing ? (
            <Form form={form} layout="vertical" initialValues={profileData}>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Name is required" }]}>
                    <Input prefix={<UserOutlined />} placeholder="Full Name" style={{ borderRadius: 2 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email is required" }]}>
                    <Input prefix={<MailOutlined />} placeholder="Email" style={{ borderRadius: 2 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[
                      {
                        pattern: /^04\d{8}$/,
                        message: "Phone must be 10 digits starting with 04",
                      },
                    ]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="04XX XXX XXX" style={{ borderRadius: 2 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="company" label="Company">
                    <Input prefix={<BankOutlined />} placeholder="Company name" disabled style={{ borderRadius: 2 }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="address" label="Address">
                    <Input
                      prefix={<EnvironmentOutlined />}
                      placeholder={isCompanyAdmin ? "Enter company address" : "Only company admins can edit"}
                      disabled={!isCompanyAdmin}
                      style={{ borderRadius: 2 }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="website"
                    label="Website"
                    rules={isCompanyAdmin ? [
                      {
                        pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                        message: "Please enter a valid website URL",
                      },
                    ] : []}
                  >
                    <Input
                      prefix={<GlobalOutlined />}
                      placeholder={isCompanyAdmin ? "www.example.com" : "Only company admins can edit"}
                      disabled={!isCompanyAdmin}
                      style={{ borderRadius: 2 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
                <Button type="primary" onClick={handleSaveProfile} icon={<SaveOutlined />} loading={saving} style={{ borderRadius: 2 }}>
                  Save Changes
                </Button>
                <Button onClick={handleCancelEdit} icon={<CloseOutlined />} style={{ borderRadius: 2 }}>
                  Cancel
                </Button>
              </div>
            </Form>
          ) : (
            <div>
              <Descriptions column={2} labelStyle={{ fontWeight: 500 }}>
                <Descriptions.Item label={<><UserOutlined style={{ marginRight: 8 }} />Full Name</>}>
                  {profileData.name}
                </Descriptions.Item>
                <Descriptions.Item label={<><MailOutlined style={{ marginRight: 8 }} />Email</>}>
                  {profileData.email}
                </Descriptions.Item>
                <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 8 }} />Phone</>}>
                  {profileData.phone || "Not provided"}
                </Descriptions.Item>
                <Descriptions.Item label={<><BankOutlined style={{ marginRight: 8 }} />Company</>}>
                  {profileData.company || "Not provided"}
                </Descriptions.Item>
                <Descriptions.Item label={<><EnvironmentOutlined style={{ marginRight: 8 }} />Address</>} span={2}>
                  {profileData.address || "Not provided"}
                </Descriptions.Item>
                <Descriptions.Item label={<><GlobalOutlined style={{ marginRight: 8 }} />Website</>}>
                  {profileData.website || "Not provided"}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ marginTop: 24 }}>
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit} style={{ borderRadius: 2 }}>
                  Edit Profile
                </Button>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "security",
      label: "Security",
      children: (
        <div style={tabContentStyle}>
          <Title level={5} style={{ marginBottom: 16 }}>Change Password</Title>
          <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword} style={{ maxWidth: 400 }}>
            {passwordStep === 1 ? (
              <>
                <Form.Item
                  name="currentPassword"
                  label="Current Password"
                  rules={[{ required: true, message: "Please enter current password" }]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="Enter your current password" style={{ borderRadius: 2 }} />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<SecurityScanOutlined />}
                  loading={verifyingPassword}
                  onClick={handleVerifyCurrentPassword}
                  style={{ borderRadius: 2 }}
                >
                  Verify & Send Code
                </Button>
              </>
            ) : (
              <>
                <div style={{ padding: "12px 16px", background: token.colorFillQuaternary, borderRadius: 8, marginBottom: 16 }}>
                  <Text type="secondary">
                    A verification code has been sent to <Text strong>{maskedEmail}</Text>
                  </Text>
                </div>
                <Form.Item
                  name="verificationCode"
                  label="Verification Code"
                  rules={[
                    { required: true, message: "Please enter the verification code" },
                    { len: 6, message: "Verification code must be 6 digits" },
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Enter 6-digit code" maxLength={6} style={{ borderRadius: 2 }} />
                </Form.Item>
                <Form.Item
                  name="newPassword"
                  label="New Password"
                  rules={[
                    { required: true, message: "Please enter new password" },
                    { min: 8, message: "Password must be at least 8 characters" },
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="New Password" style={{ borderRadius: 2 }} />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label="Confirm New Password"
                  dependencies={["newPassword"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="Confirm New Password" style={{ borderRadius: 2 }} />
                </Form.Item>
                <div style={{ display: "flex", gap: 12 }}>
                  <Button type="primary" htmlType="submit" icon={<SecurityScanOutlined />} loading={saving} style={{ borderRadius: 2 }}>
                    Change Password
                  </Button>
                  <Button onClick={handleCancelPasswordChange} style={{ borderRadius: 2 }}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>

          <Divider />

          <div style={{ padding: "12px 16px", background: token.colorFillQuaternary, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 18 }} />
              <div>
                <Text strong>Two-Factor Authentication Enabled</Text>
                <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                  Email verification is required for all security-sensitive operations
                </Paragraph>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // TODO: Notifications tab — re-enable when notification backend is ready
    // {
    //   key: "notifications",
    //   label: "Notifications",
    //   children: ( ... ),
    // },
  ];

  return (
    <PageLayout active="profile">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Profile</Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Manage your account settings and preferences
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left: Profile Card */}
        <Col xs={24} lg={8}>
          <Card bordered={false} style={{ borderRadius: 12, textAlign: "center", minHeight: 520 }}>
            <Avatar
              size={100}
              style={{
                background: `linear-gradient(135deg, ${token.colorPrimary} 0%, #3d8ab8 100%)`,
                fontSize: 36,
                marginBottom: 16,
              }}
            >
              {getInitials(profileData.name)}
            </Avatar>
            <Title level={4} style={{ marginBottom: 4 }}>{profileData.name}</Title>
            <Tag color={getRoleColor(profileData.role)} style={{ marginBottom: 16 }}>
              {profileData.role}
            </Tag>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              <MailOutlined style={{ marginRight: 8 }} />
              {profileData.email}
            </Paragraph>
            {profileData.phone && (
              <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                <PhoneOutlined style={{ marginRight: 8 }} />
                {profileData.phone}
              </Paragraph>
            )}
            {profileData.company && (
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                <BankOutlined style={{ marginRight: 8 }} />
                {profileData.company}
              </Paragraph>
            )}

            <Divider />

            <div style={{ textAlign: "left" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Text type="secondary">Account Status</Text>
                <Tag
                  color={profileData.accountStatus === "Active" ? "success" : "error"}
                  icon={<CheckCircleOutlined />}
                >
                  {profileData.accountStatus}
                </Tag>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">Member Since</Text>
                <Text>{profileData.memberSince || "Not Available"}</Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* Right: Settings Tabs */}
        <Col xs={24} lg={16}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
          </Card>
        </Col>
      </Row>

    </PageLayout>
  );
}
